import { useMemo } from 'react';
import { ArticleModel } from '../../../../../Generated/Raven/api';
import RichSelectInput, { RichSelectInputOption } from '../../../../Components/Form/Inputs/RichSelectInput';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';

const contentPreviewMaximumLength = 120;
const noneOptionKey = 'None';

const toContentPreview = (abstract?: string | null): string | undefined => {
    if (!abstract) {
        return undefined;
    }

    // Abstracts are stored as html, so markup and repeated whitespace are removed before truncating.
    const content = abstract
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (!content) {
        return undefined;
    }

    return content.length <= contentPreviewMaximumLength ? content : `${content.substring(0, contentPreviewMaximumLength).trimEnd()}...`;
};

interface IMarketIndicatorCommentaryDropdownProps {
    articles: ArticleModel[];
    areArticlesLoading: boolean;
    contentConfigurationId?: string | null;
    handleContentConfigurationIdChange: (contentConfigurationId: string | null) => void;
}

const MarketIndicatorCommentaryDropdown: React.FC<IMarketIndicatorCommentaryDropdownProps> = (props: IMarketIndicatorCommentaryDropdownProps) => {
    const [translations] = useLanguage();

    const options = useMemo(() => {
        const articlesByContentConfigurationId = new Map<string, ArticleModel>();

        props.articles.forEach((article) => {
            if (!article.contentConfigurationId) {
                return;
            }

            // Several articles can share one content configuration, so the most recently published article represents it.
            const existingArticle = articlesByContentConfigurationId.get(article.contentConfigurationId);
            if (!existingArticle || new Date(article.publishedDate) > new Date(existingArticle.publishedDate)) {
                articlesByContentConfigurationId.set(article.contentConfigurationId, article);
            }
        });

        const articleOptions: RichSelectInputOption<string | null>[] = Array.from(articlesByContentConfigurationId.values())
            .sort((first, second) => new Date(second.publishedDate).getTime() - new Date(first.publishedDate).getTime())
            .map((article) => ({
                optionKey: article.contentConfigurationId!,
                value: article.contentConfigurationId!,
                label: article.title || article.contentConfigurationId!,
                title: article.title || article.contentConfigurationId!,
                subtitle: article.author?.name ?? undefined,
                detail: formattingService.toDayOfWeekWithDayMonthYear(article.publishedDate),
                description: toContentPreview(article.abstract),
            }));

        const noneOption: RichSelectInputOption<string | null> = {
            optionKey: noneOptionKey,
            value: null,
            label: translations.words.none,
            title: translations.words.none,
        };

        const isSelectedContentConfigurationIdListed =
            !props.contentConfigurationId || articleOptions.some((option) => option.value === props.contentConfigurationId);

        // An indicator can point at a content configuration whose article is gone, that identifier stays selectable so saving does not clear it.
        const unlistedOptions: RichSelectInputOption<string | null>[] = isSelectedContentConfigurationIdListed
            ? []
            : [
                  {
                      optionKey: props.contentConfigurationId!,
                      value: props.contentConfigurationId!,
                      label: props.contentConfigurationId!,
                      title: translations.marketIndicatorsManagement.messages.articleNoLongerAvailable,
                      detail: `${translations.words.id} ${props.contentConfigurationId!}`,
                      isMuted: true,
                  },
              ];

        return [noneOption, ...articleOptions, ...unlistedOptions];
    }, [props.articles, props.contentConfigurationId, translations]);

    return (
        <RichSelectInput<string | null>
            title={translations.marketIndicatorsManagement.headers.commentaryGuid}
            value={props.contentConfigurationId ?? null}
            options={options}
            disabled={props.areArticlesLoading}
            searchPlaceholder={translations.actions.search}
            noResultsText={translations.text.noResults}
            handleOptionChange={props.handleContentConfigurationIdChange}
            track={{
                eventCategory: 'MarketIndicators',
                eventAction: 'ContentConfigurationIdChanged',
                eventValue: props.contentConfigurationId,
            }}
        />
    );
};

export default MarketIndicatorCommentaryDropdown;
